import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import apiFetch from "../../apiFetch.js";
import { supabase } from "../../supabaseClient.js";
import { T, REL } from "../../theme.js";
import { usePeople } from "../../context/PeopleContext.jsx";
import { Av, Tog, Tag, CardHead } from "../ui/index.jsx";
import { GroupScheduler, EventFormFields, RECURRENCE_OPTIONS } from "./GroupScheduler.jsx";
import { PersonDrawer } from "../people/PersonDrawer.jsx";
import {
  GS_DAYS, GS_DAY_FULL, GS_GRID_START, GS_GRID_END, GS_VISIBLE_HOURS, GS_HOUR_HEIGHT,
  GS_DATES, GS_TODAY, _CAL_MONTHS, _sunday, _nowDate, TODAY_IDX, WEEK_DATES,
  CURRENT_MONTH_NAME, MONTH_DAYS, TODAY_DAY,
  fmtWeekDate, toLocalYMD,
  gsIntersect, gsGroupOverlaps, gsFmtH, gsFmtRange, gsTotalH, gsHexRgb,
  layoutScheduleEvents,
} from "../../utils/calendarUtils.js";

// ── OneOnOneFlow ──────────────────────────────────────────────────────────────
export function OneOnOneFlow({
  isMobile, initialPersonId, initialType, weekDateNums, weekDatesObj, todayIdx,
  weekOffset, setWeekOffset, schedWeekRangeStr, onOpenPicker}){
  const {people:PEOPLE,fetchPeerAvailability,sendSchedulingRequest,userId,calEvents,setCalEvents}=usePeople();
  const [cancelOrbitEv,setCancelOrbitEv]=useState(null);
  const [cancelBusy,setCancelBusy]=useState(false);
  const [myAvail,setMyAvail]=useState(null);
  const [myEvents,setMyEvents]=useState([]);
  const initPerson = initialPersonId ? PEOPLE.find(p=>p.id===initialPersonId)||null : null;
  const [step,setStep]=useState(initPerson ? 2 : 1);
  const [person,setPerson]=useState(initPerson);
  const [slot,setSlot]=useState(null);
  const [done,setDone]=useState(false);
  const [message,setMessage]=useState("");
  const [slotView,setSlotView]=useState("calendar");
  const [tf,setTf]=useState(initialType||"all");
  const [peerAvailMap,setPeerAvailMap]=useState({});
  const [sending,setSending]=useState(false);
  const [sendError,setSendError]=useState(null);
  const [duration,setDuration]=useState(1);
  const [allDay,setAllDay]=useState(false);
  const [multiDay,setMultiDay]=useState(false);
  const [endDate,setEndDate]=useState("");
  const [recurrence,setRecurrence]=useState("none");
  const [eventTitle,setEventTitle]=useState("");
  const [confirmStartH,setConfirmStartH]=useState(null);
  const [confirmEndH,setConfirmEndH]=useState(null);
  const [startDate,setStartDate]=useState("");
  useEffect(()=>{
    if(!person?.linkedUserId||!fetchPeerAvailability) return;
    let cancelled=false;
    const from=toLocalYMD(weekDatesObj?.[0]||WEEK_DATES[0]);
    const to=toLocalYMD(weekDatesObj?.[6]||WEEK_DATES[6]);
    fetchPeerAvailability(person.linkedUserId,from,to).then(data=>{
      if(!cancelled&&data?.availability) setPeerAvailMap(m=>({...m,[person.id]:data.availability}));
    }).catch(()=>{});
    return()=>{cancelled=true;};
  },[person?.id,fetchPeerAvailability,weekOffset]);

  useEffect(()=>{
    if(!userId||!fetchPeerAvailability) return;
    const from=toLocalYMD(weekDatesObj?.[0]||WEEK_DATES[0]);
    fetchPeerAvailability(userId,from).then(data=>{setMyAvail(data?.availability||null);setMyEvents(data?.events||[]);}).catch(()=>{});
  },[weekOffset,userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered=tf==="all"?PEOPLE:PEOPLE.filter(p=>p.type===tf);
  const activeDates=weekDateNums||GS_DATES;
  const activeTodayIdx=todayIdx!==undefined?todayIdx:GS_TODAY;
  const fmtSlotDate=(di)=>{
    if(weekDatesObj&&weekDatesObj[di]){const d=weekDatesObj[di];return`${_CAL_MONTHS[d.getMonth()]} ${d.getDate()}`;}
    return fmtWeekDate(di);
  };
  const slotIsoDate=(di)=>{
    if(weekDatesObj&&weekDatesObj[di]) return toLocalYMD(weekDatesObj[di]);
    return null;
  };
  // Sync confirm times when slot changes or entering step 3
  useEffect(()=>{
    if(slot){
      setConfirmStartH(prev=>prev===null?slot.startH:prev);
      setConfirmEndH(prev=>prev===null?slot.endH:prev);
      if(!endDate){const iso=slot.isoDate||slotIsoDate(slot.dayIdx);if(iso)setEndDate(iso);}
    }
  },[slot]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendRequest=async()=>{
    if(!person||!slot) return;
    if(!person.linkedUserId){setSendError(`${person.name} hasn't joined Orbit yet — you can't send them a scheduling request.`);return;}
    setSending(true);setSendError(null);
    try{
      const isoDate=startDate||slot.isoDate||slotIsoDate(slot.dayIdx)||new Date().toISOString().slice(0,10);
      const finalStartH=allDay?0:(confirmStartH??slot.startH);
      const finalEndH=allDay?24:(confirmEndH??slot.endH);
      const effectiveDay=startDate?new Date(startDate+"T12:00:00").toLocaleDateString("en-US",{weekday:"long"}):slot.day;
      await sendSchedulingRequest({
        toUserId:person.linkedUserId,
        relationshipType:person.type,
        toDisplayName:person.name,
        proposedDay:effectiveDay,
        proposedDate:isoDate,
        proposedStartH:finalStartH,
        proposedEndH:finalEndH,
        message:message||null,
        allDay,
        title:eventTitle||undefined,
        proposedEndDate:multiDay?endDate:undefined,
        recurrence:recurrence!=="none"?{freq:recurrence==="biweekly"?"weekly":recurrence,interval:recurrence==="biweekly"?2:1}:undefined,
      });
      setDone(true);
    }catch(e){
      setSendError(e.message||"Failed to send. Please try again.");
    }finally{
      setSending(false);
    }
  };
  const SLOTS=[{day:"Tonight",date:fmtWeekDate(TODAY_IDX),start:"7 PM",end:"10 PM"},{day:"Tomorrow",date:fmtWeekDate(Math.min(TODAY_IDX+1,6)),start:"6 PM",end:"9 PM"},{day:GS_DAYS[(TODAY_IDX+2)%7],date:fmtWeekDate((TODAY_IDX+2)%7),start:"7 PM",end:"11 PM"},{day:GS_DAYS[(TODAY_IDX+3)%7],date:fmtWeekDate((TODAY_IDX+3)%7),start:"2 PM",end:"11 PM"}];

  if(done)return(<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:48,animation:"popIn .3s ease"}}>
    <div style={{width:64,height:64,borderRadius:"50%",background:T.sage+"22",border:`2px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,marginBottom:16}}>✓</div>
    <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,textAlign:"center",marginBottom:6}}>Request sent!</h2>
    <p style={{fontSize:13,color:T.muted,textAlign:"center",marginBottom:message?12:24}}>{person?.name} will get a notification to confirm.</p>
    {message&&<div style={{background:T.mist,borderRadius:10,padding:"10px 14px",marginBottom:24,maxWidth:340,width:"100%",textAlign:"left"}}>
      <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Your message</p>
      <p style={{fontSize:13,color:T.bark,lineHeight:1.5}}>{message}</p>
    </div>}
    <button onClick={()=>{setDone(false);setStep(1);setPerson(null);setSlot(null);setMessage("");}} style={{padding:"12px 28px",borderRadius:13,background:T.bark,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:"pointer"}}>Schedule another →</button>
  </div>);

  return(<>
  <div>
    {/* Step header */}
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:person&&step>1?10:16}}>
      {step>1&&<button onClick={()=>{setStep(s=>s-1);if(step===2)setSlot(null);}} style={{width:28,height:28,borderRadius:"50%",background:T.mist,border:"none",cursor:"pointer",color:T.muted,fontSize:12}}>←</button>}
      <h3 style={{fontSize:18,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>
        {step===1?"Who do you want to see?":step===2?"Pick a time":"Confirm"}
      </h3>
    </div>

    {/* Person context pill — visible on steps 2 & 3 */}
    {person&&step>1&&(()=>{
      const c=REL[person.type].color;
      return(<div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderRadius:12,background:c+"0E",border:`1px solid ${c}30`,marginBottom:16}}>
        <Av p={person} size={32}/>
        <div style={{flex:1,minWidth:0}}>
          <span style={{fontSize:13,fontWeight:600,color:T.ink}}>{person.name}</span>
          <span style={{fontSize:11,color:c,marginLeft:7}}>{REL[person.type].icon} {REL[person.type].label}</span>
          {person.avail&&<span style={{fontSize:11,color:T.sage,marginLeft:8,fontWeight:500}}>● Available now</span>}
        </div>
        <button onClick={()=>{setStep(1);setPerson(null);setSlot(null);}} style={{fontSize:11,color:T.muted,background:T.mist,border:`1px solid ${T.edge}`,borderRadius:7,padding:"3px 8px",cursor:"pointer",flexShrink:0}}>Change</button>
      </div>);
    })()}

    {step===1&&<>
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:10,marginBottom:12}}>
        <button onClick={()=>setTf("all")} style={{padding:"4px 10px",borderRadius:16,border:"none",cursor:"pointer",background:tf==="all"?T.bark:T.mist,color:tf==="all"?"white":T.muted,fontSize:11,flexShrink:0}}>All</button>
        {Object.entries(REL).map(([k,cfg])=><button key={k} onClick={()=>setTf(k)} style={{padding:"4px 9px",borderRadius:16,border:"none",cursor:"pointer",background:tf===k?cfg.color+"20":T.mist,color:tf===k?cfg.color:T.muted,fontSize:11,flexShrink:0,display:"flex",alignItems:"center",gap:3}}>{cfg.icon} {cfg.plural}</button>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:8}}>
        {filtered.map(p=>(
          <div key={p.id} onClick={()=>{setPerson(p);setStep(2);}} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:14,background:person?.id===p.id?REL[p.type].color+"12":T.surface,border:`1.5px solid ${person?.id===p.id?REL[p.type].color:T.edge}`,cursor:"pointer",transition:"all .14s"}}>
            <Av p={p} size={42}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:2}}><p style={{fontSize:13,fontWeight:600,color:T.ink}}>{p.name}</p><span style={{fontSize:11,color:REL[p.type].color,background:REL[p.type].color+"14",padding:"1px 5px",borderRadius:5}}>{REL[p.type].icon}</span></div>
              <p style={{fontSize:11,color:T.muted}}>{p.avail?"● Available now":p.next}</p>
            </div>
            <span style={{fontSize:16,color:T.edgeMid}}>›</span>
          </div>
        ))}
      </div>
    </>}

    {step===2&&person&&(()=>{
      const pid=person.id;
      const c=REL[person.type].color;
      const avail=peerAvailMap[pid]||{};
      const overlaps=gsGroupOverlaps([pid],myAvail,PEOPLE,peerAvailMap);
      const totalH=gsTotalH(overlaps);

      return(<div style={{display:"flex",flexDirection:"column",gap:10,paddingBottom:8}}>

{!person.linkedUserId&&<div style={{padding:"9px 13px",borderRadius:10,background:T.clay+"12",border:`1px solid ${T.clay}30`,fontSize:12,color:T.bark}}>
          {person.name} hasn't joined Orbit yet — their availability won't show and you won't be able to send a scheduling request.
        </div>}

        {/* View toggle + week nav bar on the same row */}
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <div style={{display:"flex",background:T.mist,borderRadius:10,padding:3,gap:2,border:`1px solid ${T.edge}`,flexShrink:0}}>
            {[{id:"calendar",label:"📅 Calendar"},{id:"windows",label:"🕐 Available times"}].map(v=>(
              <button key={v.id} onClick={()=>setSlotView(v.id)} style={{padding:"6px 14px",borderRadius:8,border:"none",cursor:"pointer",background:slotView===v.id?T.surface:"transparent",color:slotView===v.id?T.ink:T.muted,fontSize:11,fontWeight:slotView===v.id?600:400,transition:"all .14s",boxShadow:slotView===v.id?"0 1px 4px rgba(0,0,0,.08)":"none",whiteSpace:"nowrap"}}>{v.label}</button>
            ))}
          </div>
          {setWeekOffset&&<div style={{display:"flex",alignItems:"center",gap:4,marginLeft:"auto",background:T.surface,border:`1px solid ${T.edge}`,borderRadius:10,padding:"4px 8px"}}>
            <button onClick={()=>setWeekOffset(w=>w-1)} style={{width:22,height:22,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>‹</button>
            {weekOffset!==0&&<button onClick={()=>setWeekOffset(0)} style={{padding:"1px 7px",borderRadius:8,background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.bark,fontSize:11,fontWeight:600,flexShrink:0}}>Today</button>}
            <span style={{fontSize:11,fontWeight:600,color:T.ink,whiteSpace:"nowrap",padding:"0 4px"}}>{schedWeekRangeStr}</span>
            <button onClick={()=>setWeekOffset(w=>w+1)} style={{width:22,height:22,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>›</button>
            {onOpenPicker&&<button onClick={onOpenPicker} style={{width:22,height:22,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>📅</button>}
          </div>}
        </div>

        {/* Calendar view */}
        {slotView==="calendar"&&<div style={{borderRadius:12,border:`1px solid ${T.edge}`,overflow:"hidden",background:T.surface}}>
          {(()=>{
            // All-day strip: merge user's all-day events from both sources, dedup by id
            const allDayEvtsByDay=GS_DAYS.map((_,d)=>{
              const date=weekDatesObj?.[d]?toLocalYMD(weekDatesObj[d]):null;
              if(!date) return[];
              const seen=new Set();
              const evts=[];
              const candidates=[
                ...myEvents.filter(ev=>ev.allDay),
                ...calEvents.filter(ev=>ev.allDay),
              ];
              for(const ev of candidates){
                if(seen.has(ev.id)) continue;
                const evEnd=ev.endDate||ev.date;
                if(ev.date<=date&&evEnd>=date){
                  const fuzzyKey=`${(ev.label||"").toLowerCase().trim()}|${ev.date}`;
                  if(seen.has(fuzzyKey)) continue;
                  seen.add(ev.id);seen.add(fuzzyKey);evts.push(ev);
                }
              }
              return evts;
            });
            const hasAny=allDayEvtsByDay.some(evts=>evts.length>0);
            if(!hasAny) return null;
            return(
              <div style={{display:"flex",borderBottom:`1px solid ${T.edge}`}}>
                <div style={{width:38,flexShrink:0,background:T.panel,borderRight:`1px solid ${T.edge}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:"0.05em",writingMode:"vertical-rl",transform:"rotate(180deg)"}}>all day</span>
                </div>
                <div style={{flex:1,display:"flex"}}>
                  {GS_DAYS.map((_,d)=>{
                    const evts=allDayEvtsByDay[d];
                    return(
                      <div key={d} style={{flex:1,borderRight:d<6?`1px solid ${T.edge}`:"none",padding:"2px 2px",minHeight:18,display:"flex",flexDirection:"column",gap:1}}>
                        {evts.map(ev=>{
                          const isOrbit=ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-");
                          const col=isOrbit?T.orbit:T.muted;
                          return(
                            <div key={ev.id} style={{background:col+"18",borderLeft:`2px solid ${col}`,borderRadius:"0 2px 2px 0",padding:"1px 3px",overflow:"hidden"}}>
                              <span style={{fontSize:9,color:col,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",display:"block",lineHeight:1.3}}>{ev.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div style={{display:"flex",maxHeight:isMobile?380:"calc(100vh - 260px)",overflowY:"auto",overflowX:"hidden"}}>
            {/* Hour labels */}
            <div style={{width:38,flexShrink:0,paddingTop:44,background:T.panel,borderRight:`1px solid ${T.edge}`}}>
              {Array.from({length:GS_VISIBLE_HOURS},(_,i)=>(
                <div key={i} style={{height:GS_HOUR_HEIGHT,display:"flex",alignItems:"flex-start",paddingTop:2,paddingRight:5,justifyContent:"flex-end"}}>
                  <span style={{fontSize:11,color:T.muted,lineHeight:1}}>{gsFmtH(GS_GRID_START+i)}</span>
                </div>
              ))}
            </div>
            {/* Day columns */}
            <div style={{flex:1,display:"flex"}}>
              {GS_DAYS.map((_,d)=>{
                const isToday=d===activeTodayIdx;
                const mySlots=(myAvail||{})[d]||[];
                const theirSlots=avail[d]||[];
                const shared=gsIntersect(mySlots,theirSlots);
                const buckets=Array.from({length:GS_VISIBLE_HOURS*2},(_,i)=>{
                  const h=GS_GRID_START+i*0.5,he=h+0.5;
                  const me=mySlots.some(([s,e])=>s<=h&&e>=he);
                  const them=theirSlots.some(([s,e])=>s<=h&&e>=he);
                  return me&&them?3:me?1:them?2:0;
                });
                // Orbit-sched events are authoritative — any myEvents or calEvents
                // entry from another provider at the exact same time is suppressed.
                const dayOrbitSlots=calEvents.filter(ev=>ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-")&&!ev.allDay&&ev.day===d&&weekDatesObj?.[d]&&ev.date===toLocalYMD(weekDatesObj[d]));
                const overlapsOrbit=(ev)=>dayOrbitSlots.some(o=>Math.abs(o.startH-ev.startH)<0.1&&Math.abs(o.endH-ev.endH)<0.1);
                const dayMyEvts=myEvents.filter(ev=>!ev.allDay&&ev.day===d&&ev.status!=='free'&&!overlapsOrbit(ev));
                const dayCalEvts=calEvents.filter(ev=>{
                  if(ev.allDay||ev.day!==d||!weekDatesObj?.[d]||ev.date!==toLocalYMD(weekDatesObj[d])) return false;
                  if(ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-")) return true;
                  if(overlapsOrbit(ev)) return false;
                  return !dayMyEvts.some(m=>Math.abs(m.startH-ev.startH)<0.1&&Math.abs(m.endH-ev.endH)<0.1);
                });
                const handleColClick=(e)=>{
                  const rect=e.currentTarget.getBoundingClientRect();
                  const relY=e.clientY-rect.top-44;
                  if(relY<0) return; // clicked header
                  // Hit-test by Y position against orbit-sched event blocks
                  const clickH=GS_GRID_START+relY/GS_HOUR_HEIGHT;
                  const orbitHit=dayCalEvts.find(ev=>ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-")&&clickH>=ev.startH&&clickH<ev.endH);
                  if(orbitHit){setCancelOrbitEv(orbitHit);return;}
                  // Otherwise treat as a slot-picker click
                  const halfIdx=Math.max(0,Math.floor(relY/(GS_HOUR_HEIGHT/2)));
                  const h=GS_GRID_START+halfIdx*0.5;
                  const startH=h;const endH=Math.min(h+duration,GS_GRID_END);
                  const key=`${d}-${startH}-${endH}`;
                  setSlot({key,dayIdx:d,startH,endH,day:GS_DAY_FULL[d],date:fmtSlotDate(d),isoDate:slotIsoDate(d)});setStep(3);
                };
                return(<div key={d} onClick={handleColClick} style={{flex:1,borderRight:d<6?`1px solid ${T.edge}`:"none",position:"relative",minWidth:0,cursor:"pointer"}}>
                  <div style={{height:44,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderBottom:`1px solid ${T.edge}`,background:isToday?"#FDF0E6":T.panel,position:"sticky",top:0,zIndex:8}} onClick={e=>e.stopPropagation()}>
                    <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.07em",color:isToday?T.partner:T.muted,textTransform:"uppercase"}}>{GS_DAYS[d]}</span>
                    <span style={{fontSize:14,fontFamily:"'Crimson Pro',serif",fontWeight:isToday?700:400,color:isToday?T.partner:T.bark}}>{activeDates[d]}</span>
                  </div>
                  {buckets.map((b,i)=>{
                    const h=GS_GRID_START+i*0.5;
                    const inSel=slot&&slot.dayIdx===d&&h>=slot.startH&&h<slot.endH;
                    const bg=inSel?T.clay+"28":b===3?`rgba(122,158,126,${0.25})`:b===1?`rgba(193,123,78,0.13)`:b===2?`rgba(${gsHexRgb(c)},0.10)`:i%2===0?T.panel:"#F2EBE3";
                    const selMidH=slot&&slot.dayIdx===d?slot.startH+duration/2:null;
                    const showBadge=inSel&&selMidH!==null&&h<=selMidH&&h+0.5>selMidH;
                    return(<div key={i} style={{height:GS_HOUR_HEIGHT/2,background:bg,borderTop:i%2===0&&i>0?"1px solid rgba(0,0,0,0.03)":"none",position:"relative",pointerEvents:"none"}}>
                      {b===3&&<div style={{position:"absolute",left:2,top:"50%",transform:"translateY(-50%)",width:3,height:"70%",borderRadius:2,background:T.sage}}/>}
                      {showBadge&&<div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",background:T.clay,color:"white",borderRadius:20,padding:"1px 7px",fontSize:11,fontWeight:700,whiteSpace:"nowrap",zIndex:10,pointerEvents:"none"}}>✓</div>}
                    </div>);
                  })}
                  {shared.map(([s,e],i)=>{
                    const top=(s-GS_GRID_START)*GS_HOUR_HEIGHT+44;
                    const height=(e-s)*GS_HOUR_HEIGHT;
                    return(<div key={i} style={{position:"absolute",top,height:height-2,left:2,right:2,background:"transparent",border:`1.5px solid ${T.sage+"55"}`,borderRadius:6,zIndex:5,pointerEvents:"none"}}/>);
                  })}
                  {(()=>{
                    const laid=layoutScheduleEvents([...dayMyEvts.map(ev=>({...ev,_src:"my"})),...dayCalEvts.map(ev=>({...ev,_src:"cal"}))]);
                    const gap=2;
                    return laid.map((ev,i)=>{
                      const top=(ev.startH-GS_GRID_START)*GS_HOUR_HEIGHT+44;
                      const height=Math.max((ev.endH-ev.startH)*GS_HOUR_HEIGHT,6);
                      const isCal=ev._src==="cal";
                      const isOrbitSched=isCal&&ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-");
                      const accent=isOrbitSched?T.orbit:T.clay;
                      const colW=ev._totalCols>1?`calc(${100/ev._totalCols}% - ${gap*1.5}px)`:`calc(100% - ${gap*2}px)`;
                      const colL=`calc(${(ev._col/ev._totalCols)*100}% + ${gap}px)`;
                      return(<div key={`laid-${i}`}
                        data-orbit-sched-id={isOrbitSched?ev.id:undefined}
                        style={{position:"absolute",top,height:height-1,left:colL,width:colW,background:accent+"25",borderLeft:`2.5px solid ${accent}`,borderRadius:"0 3px 3px 0",zIndex:isOrbitSched?9:7,pointerEvents:"none",overflow:"hidden",paddingLeft:4,paddingTop:2,cursor:isOrbitSched?"pointer":"default"}}>
                        {height>=14&&<span style={{fontSize:11,color:accent,fontWeight:700,lineHeight:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",display:"block"}}>{isOrbitSched?"◎ ":""}{ev.label}</span>}
                      </div>);
                    });
                  })()}
                  {Array.from({length:GS_VISIBLE_HOURS},(_,i)=>(
                    <div key={i} style={{position:"absolute",top:44+i*GS_HOUR_HEIGHT,left:0,right:0,height:1,background:T.edge,zIndex:2,pointerEvents:"none"}}/>
                  ))}
                </div>);
              })}
            </div>
          </div>
          {/* Legend */}
          <div style={{borderTop:`1px solid ${T.edge}`,padding:"8px 12px 8px 50px",display:"flex",gap:14,flexWrap:"wrap",background:T.panel,flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:"rgba(122,158,126,0.4)"}}/><span style={{fontSize:11,color:T.muted}}>Both free</span></div>
            <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:"rgba(193,123,78,0.25)"}}/><span style={{fontSize:11,color:T.muted}}>Only you</span></div>
            <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:`rgba(${gsHexRgb(c)},0.2)`}}/><span style={{fontSize:11,color:T.muted}}>Only {person.name}</span></div>
            {myEvents.some(ev=>!ev.allDay&&ev.status!=='free')&&<div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:T.clay+"25",borderLeft:`2.5px solid ${T.clay}`}}/><span style={{fontSize:11,color:T.muted}}>Your events</span></div>}
            {calEvents.some(ev=>!ev.allDay&&ev.day!=null)&&<div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:T.partner+"25",borderLeft:`2.5px solid ${T.partner}`}}/><span style={{fontSize:11,color:T.muted}}>Your calendar</span></div>}
          </div>
        </div>}

        {/* Available times view */}
        {slotView==="windows"&&<div>
          {totalH===0
            ?<div style={{padding:20,borderRadius:12,background:T.mist,border:`1px solid ${T.edge}`,textAlign:"center"}}>
                <p style={{fontSize:22,marginBottom:8}}>😔</p>
                <p style={{fontSize:12,fontWeight:600,color:T.bark,marginBottom:6}}>No overlap found</p>
                <p style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:14}}>You and {person.name} don't have matching free time this week.</p>
                <button onClick={()=>setSlotView("calendar")} style={{padding:"9px 18px",borderRadius:10,background:T.clay,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>Pick any time on calendar →</button>
              </div>
            :<>
              {Object.entries(overlaps).map(([di,slots])=>{
                const subSlots=slots.flatMap(([s,e])=>{
                  const wins=[];
                  for(let t=s;t+duration<=e+0.001;t+=duration) wins.push([t,Math.min(t+duration,e)]);
                  return wins;
                });
                if(!subSlots.length)return null;
                return(<div key={di} style={{marginBottom:12}}>
                  <p style={{fontSize:11,fontWeight:700,color:T.bark,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,display:"flex",alignItems:"center",gap:5}}>
                    {GS_DAYS[+di]} · {fmtSlotDate(+di)}
                    {+di===activeTodayIdx&&<span style={{fontSize:11,background:T.partner,color:"white",padding:"1px 5px",borderRadius:6}}>today</span>}
                  </p>
                  {subSlots.map(([s,e],i)=>{
                    const key=`${di}-${s}-${e}`;
                    const isSel=slot?.key===key;
                    return(<div key={i} onClick={()=>{setSlot({key,dayIdx:+di,startH:s,endH:e,day:GS_DAY_FULL[+di],date:fmtSlotDate(+di),isoDate:slotIsoDate(+di)});setStep(3);}}
                      style={{padding:"8px 10px",borderRadius:9,marginBottom:5,background:isSel?T.sage+"18":T.mist,border:`1.5px solid ${isSel?T.sage:T.edge}`,cursor:"pointer",transition:"all .12s"}}>
                      <p style={{fontSize:12,fontWeight:700,color:T.ink}}>{gsFmtRange(s,e)}</p>
                      <p style={{fontSize:11,color:T.sage,marginTop:1}}>🕐 {e-s<1?"30m":e-s===1?"1h":e-s===1.5?"1½h":`${e-s}h`}</p>
                    </div>);
                  })}
                </div>);
              })}
              <div style={{marginTop:8,paddingTop:12,borderTop:`1px solid ${T.edge}`}}>
                <button onClick={()=>setSlotView("calendar")} style={{fontSize:11,color:T.muted,background:"transparent",border:"none",cursor:"pointer",padding:0}}>Need a different time? Pick any time on calendar →</button>
              </div>
            </>
          }
        </div>}


      </div>);
    })()}

    {step===3&&person&&slot&&(()=>{
      const sH=confirmStartH??slot.startH;
      const eH=confirmEndH??slot.endH;
      const summaryTime=allDay?"All day":gsFmtRange(sH,eH);
      const isoDate=startDate||slot.isoDate||slotIsoDate(slot.dayIdx)||"";
      const summaryDate=multiDay&&endDate&&endDate!==isoDate?`${isoDate} → ${endDate}`:slot.day;
      return(<div style={{maxWidth:520}}>
      <div style={{background:T.surface,borderRadius:16,border:`1px solid ${T.edge}`,padding:20,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <Av p={person} size={48}/>
          <div><p style={{fontSize:16,fontWeight:600,color:T.ink}}>{person.name}</p><p style={{fontSize:12,color:T.muted}}>{REL[person.type].label}</p></div>
        </div>

        {/* Event title */}
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Event title <span style={{color:T.edgeMid,fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></label>
          <input value={eventTitle} onChange={e=>setEventTitle(e.target.value)} placeholder={`Hang with ${person.name}`} style={{width:"100%",padding:"8px 12px",borderRadius:10,border:`1.5px solid ${eventTitle?T.clay+"60":T.edge}`,background:T.panel,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
        </div>

        {/* Date summary */}
        <div style={{background:T.mist,borderRadius:11,padding:"10px 14px",marginBottom:12}}>
          <p style={{fontSize:13,fontWeight:600,color:T.ink,marginBottom:2}}>{summaryDate}, {slot.date}</p>
          <p style={{fontSize:12,color:T.muted}}>{summaryTime}</p>
          {recurrence!=="none"&&<p style={{fontSize:11,color:T.sage,marginTop:3}}>Repeats {RECURRENCE_OPTIONS.find(o=>o.id===recurrence)?.label.toLowerCase()}</p>}
        </div>

        {/* Event form fields: times, all-day, multi-day, recurrence */}
        <EventFormFields startH={sH} setStartH={v=>{setConfirmStartH(v);}} endH={eH} setEndH={v=>{setConfirmEndH(v);}} allDay={allDay} setAllDay={setAllDay} multiDay={multiDay} setMultiDay={setMultiDay} endDate={endDate} setEndDate={setEndDate} startDate={startDate} setStartDate={setStartDate} recurrence={recurrence} setRecurrence={setRecurrence} slotDayIdx={slot.dayIdx} slotEndH={slot.endH} isoDate={isoDate}/>

        {/* Message */}
        <div style={{marginTop:12}}>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Message <span style={{color:T.edgeMid,fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></label>
          <textarea
            value={message}
            onChange={e=>setMessage(e.target.value)}
            placeholder={`Add a note for ${person.name}…`}
            rows={3}
            style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${message?T.clay+"60":T.edge}`,background:T.panel,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",resize:"none",outline:"none",lineHeight:1.5,boxSizing:"border-box",transition:"border-color .15s"}}
          />
        </div>
      </div>
      {(()=>{
        const sameDayOrbit=calEvents.filter(ev=>ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-")&&ev.date===isoDate&&!ev.allDay);
        if(!sameDayOrbit.length) return null;
        return(<div style={{background:T.red+"08",border:`1px solid ${T.red}22`,borderRadius:12,padding:"12px 14px",marginBottom:12}}>
          <p style={{fontSize:11,fontWeight:600,color:T.red,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Scheduled Orbit events this day</p>
          {sameDayOrbit.map(ev=>(
            <div key={ev.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:6}}>
              <span style={{fontSize:12,color:T.ink,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>◎ {ev.label} · {gsFmtH(ev.startH)}–{gsFmtH(ev.endH)}</span>
              <button onClick={()=>setCancelOrbitEv(ev)} style={{flexShrink:0,padding:"3px 10px",borderRadius:8,background:T.red+"15",border:`1px solid ${T.red}40`,color:T.red,fontSize:11,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            </div>
          ))}
        </div>);
      })()}
      {sendError&&<div style={{padding:"9px 12px",borderRadius:10,background:T.red+"12",border:`1px solid ${T.red}30`,marginBottom:10}}>
        <p style={{fontSize:12,color:T.red}}>{sendError}</p>
      </div>}
      <button onClick={handleSendRequest} disabled={sending} style={{width:"100%",padding:14,borderRadius:13,background:sending?T.edge:`linear-gradient(135deg,${T.bark},${T.clay})`,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:sending?"not-allowed":"pointer",opacity:sending?0.7:1}}>
        {sending?"Sending…":"Send request →"}
      </button>
    </div>);
    })()}
  </div>
  {cancelOrbitEv&&<div style={{position:"fixed",inset:0,background:"rgba(30,16,8,0.45)",zIndex:750,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>e.target===e.currentTarget&&setCancelOrbitEv(null)}>
    <div style={{width:"100%",maxWidth:360,background:T.surface,borderRadius:20,boxShadow:"0 12px 48px rgba(30,16,8,0.22)",overflow:"hidden"}}>
      <div style={{padding:"18px 20px 14px",borderBottom:`1px solid ${T.edge}`}}>
        <p style={{fontWeight:600,color:T.ink,marginBottom:4}}>Cancel this event?</p>
        <p style={{fontSize:13,color:T.muted}}>{cancelOrbitEv.label} · {(()=>{const d=new Date(cancelOrbitEv.date+"T12:00:00");return`${GS_DAY_FULL[d.getDay()]}, ${_CAL_MONTHS[d.getMonth()]} ${d.getDate()}, ${gsFmtH(cancelOrbitEv.startH)}–${gsFmtH(cancelOrbitEv.endH)}`;})()}</p>
      </div>
      <div style={{padding:"14px 20px",display:"flex",gap:8}}>
        <button onClick={()=>setCancelOrbitEv(null)} style={{flex:1,padding:11,borderRadius:11,background:"transparent",border:`1px solid ${T.edge}`,color:T.muted,fontSize:13,cursor:"pointer"}}>Keep</button>
        <button disabled={cancelBusy} onClick={async()=>{
          const ev=cancelOrbitEv;
          const reqId=ev.id.replace(/^orbit-sched-/,"").replace(/-d\d+$/,"");
          setCancelBusy(true);
          try{
            const {data:{session}}=await supabase.auth.getSession();
            if(session?.access_token) await apiFetch("/api/scheduling/cancel",{method:"PATCH",headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},body:JSON.stringify({request_id:reqId})});
          }catch(_){}
          setCalEvents(prev=>prev.filter(e=>!(e.source==="orbit"&&(e.id===`orbit-sched-${reqId}`||e.id?.startsWith(`orbit-sched-${reqId}-d`)))));
          setCancelBusy(false);setCancelOrbitEv(null);
        }} style={{flex:2,padding:11,borderRadius:11,background:cancelBusy?T.edge:"#C0392B",border:"none",color:"white",fontSize:13,fontWeight:600,cursor:cancelBusy?"default":"pointer"}}>
          {cancelBusy?"Cancelling…":"Confirm cancellation"}
        </button>
      </div>
    </div>
  </div>}
  </>);
}

// ── ScheduleTabContent ────────────────────────────────────────────────────────
export function ScheduleTabContent({onNav,isMobile,initialPersonId,initialType,initialMode}){
  const [mode,setMode]=useState(initialMode||"1:1");
  const [weekOffset,setWeekOffset]=useState(0);
  const [showPicker,setShowPicker]=useState(false);
  const [pickerMonthOffset,setPickerMonthOffset]=useState(0);

  const schedMonday=useMemo(()=>{const m=new Date(_sunday);m.setDate(_sunday.getDate()+weekOffset*7);return m;},[weekOffset]);
  const schedWeekDates=useMemo(()=>Array.from({length:7},(_,i)=>{const d=new Date(schedMonday);d.setDate(schedMonday.getDate()+i);return d;}),[schedMonday]);
  const schedDateNums=schedWeekDates.map(d=>d.getDate());
  const schedTodayIdx=weekOffset===0?TODAY_IDX:-1;
  const schedWeekRangeStr=schedWeekDates[0].getMonth()===schedWeekDates[6].getMonth()
    ?`${_CAL_MONTHS[schedWeekDates[0].getMonth()]} ${schedDateNums[0]}–${schedDateNums[6]}`
    :`${_CAL_MONTHS[schedWeekDates[0].getMonth()]} ${schedDateNums[0]} – ${_CAL_MONTHS[schedWeekDates[6].getMonth()]} ${schedDateNums[6]}`;

  const pickerBase=useMemo(()=>new Date(_nowDate.getFullYear(),_nowDate.getMonth()+pickerMonthOffset,1),[pickerMonthOffset]);
  const pickerDays=useMemo(()=>{
    const firstDow=pickerBase.getDay();
    const firstVisible=new Date(pickerBase.getFullYear(),pickerBase.getMonth(),1-firstDow);
    return Array.from({length:42},(_,i)=>{const d=new Date(firstVisible);d.setDate(firstVisible.getDate()+i);return d;});
  },[pickerBase]);

  const modes=[
    {id:"1:1",   icon:"👤", label:"1:1",          sub:"One-on-one with anyone"},
    {id:"group", icon:"👥", label:"Group hang",   sub:"Find when everyone's free"},
  ];

  return(<div>
    <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:12}}>
      <div>
        <h1 style={{fontSize:28,fontFamily:"'Crimson Pro',serif",fontWeight:300,letterSpacing:"-0.02em"}}>Schedule</h1>
        <p style={{fontSize:13,color:T.muted,marginTop:4}}>Coordinate time with anyone in your life</p>
      </div>
      <div style={{display:"flex",background:"transparent",borderRadius:20,padding:2,gap:2,flexShrink:0}}>
        {modes.map(m=>(
          <button key={m.id} onClick={()=>setMode(m.id)} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:20,border:"none",cursor:"pointer",background:mode===m.id?T.bark:"transparent",color:mode===m.id?T.parchment:T.muted,fontSize:12,fontWeight:mode===m.id?600:400,transition:"all .15s",margin:"4px 2px"}}>
            <span style={{fontSize:11}}>{m.icon}</span>{m.label}
          </button>
        ))}
      </div>
    </div>

    {showPicker&&(
      <div onClick={()=>setShowPicker(false)} style={{position:"fixed",inset:0,background:"rgba(30,16,8,0.4)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div onClick={e=>e.stopPropagation()} style={{background:T.surface,borderRadius:16,padding:20,width:284,boxShadow:"0 8px 40px rgba(0,0,0,.18)",animation:"fadeUp .15s ease"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <button onClick={()=>setPickerMonthOffset(m=>m-1)} style={{width:28,height:28,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:14}}>‹</button>
            <span style={{fontSize:14,fontWeight:600,color:T.ink}}>{_CAL_MONTHS[pickerBase.getMonth()]} {pickerBase.getFullYear()}</span>
            <button onClick={()=>setPickerMonthOffset(m=>m+1)} style={{width:28,height:28,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:14}}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
            {["M","T","W","T","F","S","S"].map((d,i)=>(
              <div key={i} style={{textAlign:"center",fontSize:11,fontWeight:700,color:T.muted}}>{d}</div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {pickerDays.map((d,i)=>{
              const isCurMonth=d.getMonth()===pickerBase.getMonth();
              const todayStr=`${_nowDate.getFullYear()}-${String(_nowDate.getMonth()+1).padStart(2,'0')}-${String(_nowDate.getDate()).padStart(2,'0')}`;
              const dStr=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const isToday=dStr===todayStr;
              const inSelectedWeek=d>=schedMonday&&d<new Date(schedMonday.getTime()+7*86400000);
              return(
                <div key={i} onClick={()=>{
                  const df=Math.round((d-_sunday)/86400000);
                  setWeekOffset(Math.floor(df/7));
                  setShowPicker(false);
                }} style={{
                  textAlign:"center",fontSize:11,padding:"5px 2px",borderRadius:6,cursor:"pointer",
                  background:inSelectedWeek?T.partner+"18":isToday?"#FDF0E6":"transparent",
                  border:`1px solid ${inSelectedWeek?T.partner+"40":isToday?T.partner+"30":"transparent"}`,
                  color:isCurMonth?(isToday?T.partner:T.ink):T.muted,
                  fontWeight:isToday?700:400,
                  transition:"background .1s"
                }}>
                  {d.getDate()}
                </div>
              );
            })}
          </div>
          <button onClick={()=>{setWeekOffset(0);setPickerMonthOffset(0);setShowPicker(false);}} style={{width:"100%",marginTop:12,padding:"8px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",fontSize:12,fontWeight:600,color:T.bark}}>Go to today</button>
        </div>
      </div>
    )}

    {mode==="1:1" && <OneOnOneFlow isMobile={isMobile} initialPersonId={initialPersonId} initialType={initialType} weekDateNums={schedDateNums} weekDatesObj={schedWeekDates} todayIdx={schedTodayIdx} weekOffset={weekOffset} setWeekOffset={setWeekOffset} schedWeekRangeStr={schedWeekRangeStr} onOpenPicker={()=>{setPickerMonthOffset(0);setShowPicker(true);}}/>}
    {mode==="group" && <GroupScheduler isMobile={isMobile} weekDateNums={schedDateNums} weekDatesObj={schedWeekDates} todayIdx={schedTodayIdx} weekOffset={weekOffset} setWeekOffset={setWeekOffset} schedWeekRangeStr={schedWeekRangeStr} onOpenPicker={()=>{setPickerMonthOffset(0);setShowPicker(true);}}/>}
  </div>);
}

// ── ScheduleContent ───────────────────────────────────────────────────────────
export function ScheduleContent({onNav,isMobile,schedulePersonId,scheduleType,scheduleMode}){
  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"clip"}}>
      <div style={{flex:1,overflowY:"auto",padding:isMobile?"14px 18px":"20px 28px"}}>
        <ScheduleTabContent onNav={onNav} isMobile={isMobile} initialPersonId={schedulePersonId} initialType={scheduleType} initialMode={scheduleMode}/>
      </div>
    </div>
  );
}

// ── Fairness helpers ──────────────────────────────────────────────────────────
function monthlyTgt(p){ return p.tgtMode==="week"?Math.round(p.tgt*4.33):p.tgt; }
function fScore(p){
  const tgt=monthlyTgt(p);
  const ratio=tgt>0?p.act/tgt:1;
  const expected=tgt*(TODAY_DAY/MONTH_DAYS);
  return Math.max(0,Math.min(100,50+(p.act-expected)*18));
}
function uScore(p){
  const tgt=monthlyTgt(p);
  const needed=tgt-p.act;
  if(needed<=0)return 0;
  const rem=MONTH_DAYS-TODAY_DAY;
  if(rem<=0)return 100;
  return Math.min(100,(needed/rem)*150);
}

// ── OrbitalChart ──────────────────────────────────────────────────────────────
function OrbitalChart({partners,selectedId,onSelect}){
  const canvasRef=useRef(null);
  const animRef=useRef(null);

  const draw=useCallback((t)=>{
    const canvas=canvasRef.current; if(!canvas)return;
    const ctx=canvas.getContext("2d");
    const W=canvas.width,H=canvas.height,cx=W/2,cy=H/2;
    ctx.clearRect(0,0,W,H);

    const grd=ctx.createRadialGradient(cx,cy,0,cx,cy,Math.min(W,H)*0.52);
    grd.addColorStop(0,"rgba(193,123,78,0.07)");
    grd.addColorStop(1,"rgba(0,0,0,0)");
    ctx.fillStyle=grd; ctx.fillRect(0,0,W,H);

    const meR=26;
    ctx.beginPath(); ctx.arc(cx,cy,meR,0,Math.PI*2);
    const meG=ctx.createRadialGradient(cx-4,cy-4,2,cx,cy,meR);
    meG.addColorStop(0,"#E8D5BC"); meG.addColorStop(1,"#C17B4E");
    ctx.fillStyle=meG; ctx.fill();
    ctx.font="bold 10px 'DM Sans',sans-serif";
    ctx.fillStyle="#1A1208"; ctx.textAlign="center"; ctx.textBaseline="middle";
    ctx.fillText("ME",cx,cy);

    partners.forEach((p,i)=>{
      const ratio=p.tgt>0?p.act/monthlyTgt(p):1;
      const urgency=uScore(p);
      const orbitR=58+(partners.length-1-i)*32+(Math.max(0,8-p.tgt)*3);

      ctx.beginPath(); ctx.arc(cx,cy,orbitR,0,Math.PI*2);
      ctx.strokeStyle=p.color+"20"; ctx.lineWidth=1.5;
      ctx.setLineDash([4,9]); ctx.stroke(); ctx.setLineDash([]);

      const base=(i/partners.length)*Math.PI*2-Math.PI/2;
      const drift=p.anchorNight?0:Math.sin(t*0.00018+i*1.4)*0.1;
      const angle=base+drift;
      const px=cx+Math.cos(angle)*orbitR;
      const py=cy+Math.sin(angle)*orbitR;

      const alpha=0.12+ratio*0.38;
      ctx.beginPath(); ctx.moveTo(cx,cy); ctx.lineTo(px,py);
      ctx.strokeStyle=p.color+Math.round(alpha*255).toString(16).padStart(2,"0");
      ctx.lineWidth=0.5+ratio*2.2; ctx.stroke();

      if(urgency>25){
        const gr=17+urgency*0.2;
        const gg=ctx.createRadialGradient(px,py,0,px,py,gr);
        gg.addColorStop(0,p.color+"50"); gg.addColorStop(1,"transparent");
        ctx.beginPath(); ctx.arc(px,py,gr,0,Math.PI*2);
        ctx.fillStyle=gg; ctx.fill();
      }

      if(selectedId===p.id){
        ctx.beginPath(); ctx.arc(px,py,21,0,Math.PI*2);
        ctx.strokeStyle=p.color+"CC"; ctx.lineWidth=2; ctx.stroke();
      }

      const nodeR=15;
      ctx.beginPath(); ctx.arc(px,py,nodeR,0,Math.PI*2);
      const ng=ctx.createRadialGradient(px-3,py-3,1,px,py,nodeR);
      ng.addColorStop(0,p.color+"EE"); ng.addColorStop(1,p.color+"88");
      ctx.fillStyle=ng; ctx.fill();

      ctx.beginPath();
      ctx.arc(px,py,nodeR+3,-Math.PI/2,-Math.PI/2+Math.PI*2*Math.min(ratio,1));
      ctx.strokeStyle=p.color; ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.stroke();

      ctx.font="bold 8px 'DM Sans',sans-serif";
      ctx.fillStyle="#0F0B08"; ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText(p.ini||p.name[0],px,py);

      const offX=Math.cos(angle)*30, offY=Math.sin(angle)*28;
      ctx.font="500 10px 'DM Sans',sans-serif";
      ctx.fillStyle=selectedId===p.id?p.color:"#9A7A60";
      ctx.textAlign=offX>4?"left":offX<-4?"right":"center";
      ctx.fillText(p.name,px+offX,py+offY);
      ctx.font="400 9px 'DM Sans',sans-serif";
      ctx.fillStyle="#6B5040";
      ctx.fillText(`${p.act}/${p.tgt}`,px+offX,py+offY+12);

      if(p.anchorNight){
        ctx.beginPath(); ctx.arc(px+nodeR-2,py-nodeR+2,5,0,Math.PI*2);
        ctx.fillStyle="#C4A050"; ctx.fill();
        ctx.font="bold 6px sans-serif"; ctx.fillStyle="#0F0B08";
        ctx.textAlign="center"; ctx.textBaseline="middle";
        ctx.fillText("⚓",px+nodeR-2,py-nodeR+2);
      }

      p._cx=px; p._cy=py;
    });

    animRef.current=requestAnimationFrame(nt=>draw(nt));
  },[partners,selectedId]);

  useEffect(()=>{
    animRef.current=requestAnimationFrame(t=>draw(t));
    return()=>cancelAnimationFrame(animRef.current);
  },[draw]);

  function handleClick(e){
    const canvas=canvasRef.current;
    const rect=canvas.getBoundingClientRect();
    const sx=canvas.width/rect.width, sy=canvas.height/rect.height;
    const mx=(e.clientX-rect.left)*sx, my=(e.clientY-rect.top)*sy;
    for(const p of partners){
      if(p._cx===undefined)continue;
      const dx=mx-p._cx,dy=my-p._cy;
      if(Math.sqrt(dx*dx+dy*dy)<20){onSelect(p.id===selectedId?null:p.id);return;}
    }
    onSelect(null);
  }

  return(<canvas ref={canvasRef} width={340} height={340} onClick={handleClick}
    style={{width:"100%",maxWidth:340,cursor:"pointer",display:"block"}}/>);
}

// ── FairnessContent ───────────────────────────────────────────────────────────
export function FairnessContent({onNav,isMobile}){
  const {people:PEOPLE,prefs}=usePeople();
  const [tab,setTab]=useState("partners");
  const [drawerId,setDrawerId]=useState(null);
  const partners=PEOPLE.filter(p=>p.type==="partner"&&p.tgt);
  const family=PEOPLE.filter(p=>p.type==="family");
  const friends=PEOPLE.filter(p=>p.type==="friend");
  const pad=isMobile?"8px 18px 0":"12px 28px 0";
  const partnerCount=PEOPLE.filter(p=>p.type==="partner").length;

  const mostDue=partners.length?[...partners].sort((a,b)=>(a.act/a.tgt)-(b.act/b.tgt))[0]:null;

  const [anchors,setAnchors]=useState([]);
  const ANCHOR_DAYS=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  const tabItems=[
    {id:"partners",cfg:REL.partner},
    {id:"family",cfg:REL.family},
    {id:"friends",cfg:REL.friend},
    ...(partnerCount>1&&prefs?.anchorNights!==false?[{id:"anchors",cfg:{icon:"⚓",plural:"Anchors",color:T.bark}}]:[]),
  ];

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <div style={{padding:pad,flexShrink:0}}>
      <h2 style={{fontSize:isMobile?22:26,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:8}}>Fairness & Rhythm</h2>
      <p style={{fontSize:12,color:T.muted,marginBottom:10}}>Partners tracked by nights · Family & friends tracked by recency</p>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}>
        {tabItems.map(({id,cfg})=>(
          <button key={id} onClick={()=>setTab(id)} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 13px",borderRadius:20,border:"none",cursor:"pointer",background:tab===id?T.bark:"transparent",color:tab===id?T.parchment:T.muted,fontSize:11,fontWeight:tab===id?600:400,transition:"all .14s",margin:"4px 2px"}}>
            <span>{cfg.icon}</span>{cfg.plural}
          </button>
        ))}
      </div>
    </div>
    <div style={{flex:1,overflowY:"auto",padding:isMobile?"12px 18px":"16px 28px"}}>

      {tab==="partners"&&(<div style={{animation:"fadeUp .25s ease"}}>
        {!prefs?.fairnessTracking&&<div style={{background:T.mist,borderRadius:12,padding:"12px 16px",marginBottom:16,border:`1px solid ${T.edge}`,textAlign:"center"}}>
          <p style={{fontSize:13,color:T.muted}}>Fairness tracking is off — enable it in Settings → Preferences.</p>
        </div>}

        {prefs?.fairnessTracking&&<div style={{background:T.surface,borderRadius:16,border:`1px solid ${T.edge}`,overflow:"hidden",marginBottom:16,boxShadow:"0 2px 12px rgba(60,40,20,.05)"}}>
          <CardHead icon="♥" label={`Partner nights — ${CURRENT_MONTH_NAME}`} sub="Target vs actual nights spent"/>
          {partners.map((p,i)=>{
            const mtgt=monthlyTgt(p);
            const pct=Math.round((p.act/mtgt)*100);
            const urgency=uScore(p);
            const color=pct>=90?T.sage:pct>=60?T.amber:T.red;
            return(<div key={p.id} onClick={()=>setDrawerId(p.id)} style={{padding:"14px 16px",borderBottom:i<partners.length-1?`1px solid ${T.edge}`:"none",cursor:"pointer",transition:"background .14s"}}
              onMouseEnter={e=>e.currentTarget.style.background=T.mist}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <Av p={p} size={38}/>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,fontWeight:600,color:T.ink}}>{p.name}</p>
                  <div style={{display:"flex",gap:4,marginTop:2}}>{p.tags.map(t=><Tag key={t} label={t} color={REL.partner.color}/>)}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <span style={{fontSize:18,fontWeight:700,color,fontFamily:"'Crimson Pro',serif"}}>{pct}%</span>
                  <p style={{fontSize:11,color:T.muted}}>{p.act}/{mtgt} nights{p.tgtMode==="week"&&<span style={{color:T.muted+"88"}}> ({p.tgt}/wk)</span>}</p>
                </div>
              </div>
              <div style={{position:"relative",height:7,borderRadius:4,background:T.edge,overflow:"visible"}}>
                <div style={{position:"absolute",top:-3,bottom:-3,left:`${Math.min(TODAY_DAY/MONTH_DAYS*100,100)}%`,width:2,borderRadius:1,background:T.muted+"80",transform:"translateX(-50%)"}}/>
                <div style={{height:"100%",borderRadius:4,width:`${Math.min(100,pct)}%`,background:`linear-gradient(90deg,${color}88,${color})`,transition:"width .6s ease"}}/>
              </div>
              {urgency>40&&<p style={{fontSize:11,color:T.amber,marginTop:4}}>↑ {Math.round(urgency)}% urgency — {Math.ceil(mtgt-p.act)} nights still needed</p>}
            </div>);
          })}
        </div>}

        {prefs?.fairnessTracking&&mostDue&&<div style={{background:T.partner+"0A",borderRadius:14,padding:16,border:`1px solid ${T.partner}25`,display:"flex",alignItems:"center",gap:12}}>
          <Av p={mostDue} size={40}/>
          <div style={{flex:1}}>
            <p style={{fontSize:13,fontWeight:600,color:T.bark}}>♥ Who's next?</p>
            <p style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Based on fairness targets, <strong>{mostDue.name}</strong> is due for the most nights this month.</p>
          </div>
          <button onClick={(e)=>{e.stopPropagation();onNav("schedule",mostDue.id);}} style={{padding:"9px 16px",borderRadius:10,background:T.partner,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>Schedule →</button>
        </div>}
      </div>)}

      {tab==="family"&&(<div style={{animation:"fadeUp .25s ease"}}>
        <div style={{background:T.surface,borderRadius:16,border:`1px solid ${T.edge}`,overflow:"hidden",marginBottom:16,boxShadow:"0 2px 12px rgba(60,40,20,.05)"}}>
          <CardHead icon="👨‍👩‍👧" label="Family — recency" sub="No fairness targets, just gentle nudges when it's been a while"/>
          {family.map((p,i)=>(
            <div key={p.id} onClick={()=>setDrawerId(p.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:i<family.length-1?`1px solid ${T.edge}`:"none",cursor:"pointer",transition:"background .14s"}}
              onMouseEnter={e=>e.currentTarget.style.background=T.mist}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Av p={p} size={38}/>
              <div style={{flex:1}}>
                <p style={{fontSize:13,fontWeight:500,color:T.ink}}>{p.name}</p>
                <div style={{display:"flex",gap:4,marginTop:2}}>{p.tags.map(t=><Tag key={t} label={t} color={T.family}/>)}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5}}>
                <span style={{fontSize:12,fontWeight:600,color:p.seen>10?T.amber:T.sage,background:(p.seen>10?T.amber:T.sage)+"18",padding:"3px 9px",borderRadius:10,border:`1px solid ${(p.seen>10?T.amber:T.sage)}35`}}>{p.seen ?? 0}d ago</span>
                {p.seen>10&&<button onClick={(e)=>{e.stopPropagation();onNav("schedule",p.id);}} style={{padding:"4px 10px",borderRadius:9,border:"none",cursor:"pointer",background:T.family+"18",color:T.family,fontSize:11,fontWeight:600}}>Schedule</button>}
              </div>
            </div>
          ))}
        </div>
      </div>)}

      {tab==="friends"&&(<div style={{animation:"fadeUp .25s ease"}}>
        <div style={{background:T.surface,borderRadius:16,border:`1px solid ${T.edge}`,overflow:"hidden",marginBottom:16,boxShadow:"0 2px 12px rgba(60,40,20,.05)"}}>
          <CardHead icon="👥" label="Friends — drift detection" sub="Flags when you haven't connected in a while"/>
          {friends.map((p,i)=>(
            <div key={p.id} onClick={()=>setDrawerId(p.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:i<friends.length-1?`1px solid ${T.edge}`:"none",background:p.seen>14?T.amber+"06":"transparent",cursor:"pointer",transition:"background .14s"}}
              onMouseEnter={e=>e.currentTarget.style.background=p.seen>14?T.amber+"10":T.mist}
              onMouseLeave={e=>e.currentTarget.style.background=p.seen>14?T.amber+"06":"transparent"}>
              <Av p={p} size={38}/>
              <div style={{flex:1}}>
                <p style={{fontSize:13,fontWeight:500,color:T.ink}}>{p.name}</p>
                <div style={{display:"flex",gap:4,marginTop:2}}>{p.tags.map(t=><Tag key={t} label={t} color={T.friend}/>)}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5}}>
                <span style={{fontSize:12,fontWeight:600,color:p.seen>21?T.red:p.seen>14?T.amber:T.sage,background:(p.seen>21?T.red:p.seen>14?T.amber:T.sage)+"18",padding:"3px 9px",borderRadius:10}}>{p.seen ?? 0}d ago</span>
                {p.seen>14&&<button onClick={(e)=>{e.stopPropagation();onNav("schedule",p.id);}} style={{padding:"4px 10px",borderRadius:9,border:"none",cursor:"pointer",background:T.friend+"18",color:T.friend,fontSize:11,fontWeight:600}}>Reach out</button>}
              </div>
            </div>
          ))}
        </div>
      </div>)}

      {tab==="anchors"&&partnerCount>1&&prefs?.anchorNights!==false&&(<div style={{animation:"fadeUp .25s ease"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:16}}>
          {ANCHOR_DAYS.map((d,i)=>{
            const a=anchors.find(x=>x.active&&((x.day==="Tuesday"&&i===1)||(x.day==="Thursday"&&i===3)||(x.day==="Saturday"&&i===5)));
            return(<div key={d} style={{minHeight:52,borderRadius:8,background:a?a.color+"14":T.mist,border:`1px solid ${a?a.color+"35":T.edge}`,padding:"4px 3px",textAlign:"center"}}>
              <p style={{fontSize:11,fontWeight:i===2?700:400,color:i===2?T.partner:T.muted,marginBottom:3}}>{d}</p>
              {a&&<div style={{width:8,height:8,borderRadius:"50%",background:a.color,margin:"0 auto"}}/>}
            </div>);
          })}
        </div>
        {anchors.map(a=>(
          <div key={a.id} style={{padding:"14px 16px",borderRadius:16,marginBottom:10,background:a.active?T.surface:T.mist,border:`1.5px solid ${a.active?a.color+"40":T.edge}`,opacity:a.active?1:0.7}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <div style={{width:40,height:40,borderRadius:"50%",background:a.color+"20",border:`2px solid ${a.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:a.color,fontFamily:"'Crimson Pro',serif",flexShrink:0,overflow:"hidden"}}>{a.photo?<img src={a.photo} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} alt=""/>:a.ini}</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                  <p style={{fontSize:14,fontWeight:600,color:T.ink}}>{a.day} {a.type.toLowerCase()}s</p>
                  {!a.active&&<span style={{fontSize:11,color:T.muted,background:T.edge,padding:"1px 6px",borderRadius:8,textTransform:"uppercase"}}>paused</span>}
                </div>
                <p style={{fontSize:12,color:T.muted}}>with {a.name} · {a.time}</p>
              </div>
              <Tog on={a.active} set={v=>setAnchors(arr=>arr.map(x=>x.id===a.id?{...x,active:v}:x))} c={a.color}/>
            </div>
            {a.active&&<div style={{display:"flex",gap:8}}>
              <button onClick={()=>onNav("schedule")} style={{flex:1,padding:"7px 0",borderRadius:9,background:a.color+"14",border:`1px solid ${a.color}30`,color:a.color,fontSize:12,fontWeight:600,cursor:"pointer"}}>Schedule next</button>
              <button style={{flex:1,padding:"7px 0",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer"}}>Edit</button>
            </div>}
          </div>
        ))}
      </div>)}

    </div>
    {drawerId&&<PersonDrawer isMobile={isMobile} personId={drawerId} onClose={()=>setDrawerId(null)} onSchedule={()=>{const id=drawerId;setDrawerId(null);onNav("schedule",id);}}/>}
  </div>);
}

// ── AnchorsContent ────────────────────────────────────────────────────────────
export function AnchorsContent({onNav,isMobile}){
  const [anchors,setAnchors]=useState([]);
  const DAYS=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const pad=isMobile?"8px 18px 0":"12px 28px 0";
  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
    <div style={{padding:pad,flexShrink:0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <h2 style={{fontSize:isMobile?22:26,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>Anchor nights</h2>
        <button style={{padding:"6px 14px",borderRadius:10,background:T.clay,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>+ Add anchor</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:4}}>
        {DAYS.map((d,i)=>{
          const a=anchors.find(x=>x.active&&((x.day==="Tuesday"&&i===1)||(x.day==="Thursday"&&i===3)||(x.day==="Saturday"&&i===5)));
          return(<div key={d} style={{minHeight:52,borderRadius:8,background:a?a.color+"14":T.mist,border:`1px solid ${a?a.color+"35":T.edge}`,padding:"4px 3px",textAlign:"center"}}>
            <p style={{fontSize:11,fontWeight:i===2?700:400,color:i===2?T.partner:T.muted,marginBottom:3}}>{d}</p>
            {a&&<div style={{width:8,height:8,borderRadius:"50%",background:a.color,margin:"0 auto"}}/>}
          </div>);
        })}
      </div>
    </div>
    <div style={{flex:1,overflowY:"auto",padding:isMobile?"10px 18px":"12px 28px"}}>
      {anchors.length===0&&(
        <div style={{padding:"48px 24px",textAlign:"center",background:T.surface,borderRadius:16,border:`1px solid ${T.edge}`}}>
          <p style={{fontSize:28,marginBottom:10}}>⚓</p>
          <h3 style={{fontSize:18,fontFamily:"'Crimson Pro',serif",fontWeight:300,marginBottom:6,color:T.ink}}>No anchor nights yet</h3>
          <p style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Anchor nights are recurring protected time with a partner. Add one to get started.</p>
        </div>
      )}
      {anchors.map(a=>(
        <div key={a.id} style={{padding:"14px 16px",borderRadius:16,marginBottom:10,background:a.active?T.surface:T.mist,border:`1.5px solid ${a.active?a.color+"40":T.edge}`,opacity:a.active?1:0.7}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:40,height:40,borderRadius:"50%",background:a.color+"20",border:`2px solid ${a.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:a.color,fontFamily:"'Crimson Pro',serif",flexShrink:0,overflow:"hidden"}}>{a.photo?<img src={a.photo} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} alt=""/>:a.ini}</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                <p style={{fontSize:14,fontWeight:600,color:T.ink}}>{a.day} {a.type.toLowerCase()}s</p>
                {!a.active&&<span style={{fontSize:11,color:T.muted,background:T.edge,padding:"1px 6px",borderRadius:8,textTransform:"uppercase"}}>paused</span>}
              </div>
              <p style={{fontSize:12,color:T.muted}}>with {a.name} · {a.time}</p>
            </div>
            <Tog on={a.active} set={v=>setAnchors(arr=>arr.map(x=>x.id===a.id?{...x,active:v}:x))} c={a.color}/>
          </div>
          {a.active&&<div style={{display:"flex",gap:8}}>
            <button onClick={()=>onNav("schedule")} style={{flex:1,padding:"7px 0",borderRadius:9,background:a.color+"14",border:`1px solid ${a.color}30`,color:a.color,fontSize:12,fontWeight:600,cursor:"pointer"}}>Schedule next</button>
            <button style={{flex:1,padding:"7px 0",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer"}}>Edit</button>
          </div>}
        </div>
      ))}
    </div>
  </div>);
}
